import { useSEO, breadcrumb } from "../lib/seo";
import { SITE, ASSETS } from "../lib/site";
import PageHero from "../components/PageHero";
import { ShieldCheck, Globe2, BookOpen, Sparkles, Users, Compass } from "lucide-react";

export default function About() {
  useSEO({
    title: "Tentang Sloto69 | Company Profile Resmi",
    description: "Pelajari tentang Sloto69 — brand digital berbahasa Indonesia yang fokus pada informasi, panduan, dan keamanan pengguna.",
    keywords: "tentang sloto69, company profile sloto69, sloto69 indonesia, profil sloto69",
    canonical: `${SITE.baseUrl}/tentang`,
    jsonLd: breadcrumb([
      { name: "Beranda", url: `${SITE.baseUrl}/` },
      { name: "Tentang Kami", url: `${SITE.baseUrl}/tentang` },
    ]),
  });

  const VALUES = [
    { icon: BookOpen, title: "Edukatif", desc: "Konten ditulis untuk membantu pengguna memahami informasi penting secara jelas." },
    { icon: ShieldCheck, title: "Aman & Tepercaya", desc: "Kami menjunjung tinggi prinsip keamanan dan kewaspadaan terhadap situs tiruan." },
    { icon: Globe2, title: "Bahasa Indonesia", desc: "100% bahasa Indonesia, formal namun ramah, agar mudah dipahami semua pengguna." },
    { icon: Sparkles, title: "Modern & Ringan", desc: "Antarmuka modern dengan performa ringan agar nyaman dibuka di berbagai perangkat." },
    { icon: Users, title: "Berorientasi Pengguna", desc: "Setiap halaman dirancang dari perspektif pengguna agar bermanfaat secara langsung." },
    { icon: Compass, title: "Konsisten Update", desc: "Kami memperbarui informasi dan link alternatif secara berkala demi kelancaran akses." },
  ];

  return (
    <>
      <PageHero
        eyebrow="Tentang Kami"
        title="Mengenal Sloto69 sebagai Pusat Informasi Resmi Berbahasa Indonesia"
        description="Sloto69 adalah brand digital yang fokus pada kemudahan akses, kelengkapan informasi, kenyamanan pengguna, dan pembaruan konsisten dalam satu website company profile."
        breadcrumb={[{ label: "Tentang Kami" }]}
      />

      <section className="section-pad" data-testid="about-overview">
        <div className="shell grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-6 text-slate-300 leading-relaxed text-base">
            <h2 className="font-display text-2xl sm:text-3xl text-white">Komitmen Sloto69</h2>
            <p>
              Sloto69 berkomitmen menyajikan informasi resmi melalui website company profile
              yang dirancang untuk pengguna Indonesia. Kami fokus pada penyajian konten yang
              jelas, terstruktur, dan tidak menyesatkan. Pengguna dapat menemukan panduan
              langkah demi langkah, edukasi keamanan, hingga referensi link alternatif terbaru.
            </p>
            <p>
              Pendekatan kami sederhana: utamakan kualitas informasi dan pengalaman pengguna.
              Itulah sebabnya tampilan website ini ringan, modern, dan minim distraksi. Tidak
              ada elemen berlebihan yang dapat memperlambat akses, terutama bagi pengguna mobile.
            </p>
            <h2 className="font-display text-2xl sm:text-3xl text-white pt-4">Bagaimana Kami Bekerja</h2>
            <p>
              Tim kami terus melakukan kurasi konten, menyaring informasi, dan memastikan setiap
              halaman dapat dipertanggungjawabkan. Kami juga menyediakan kanal komunikasi yang
              transparan agar pengguna dapat menghubungi kami kapan pun memerlukan klarifikasi
              atau bantuan tambahan.
            </p>
            <p>
              Kami percaya bahwa kepercayaan pengguna dibangun dari konsistensi. Karena itu,
              kami akan terus memperluas konten edukatif, memperbarui daftar link resmi, dan
              meningkatkan kualitas pengalaman pengguna.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="card-premium p-2">
              <img
                src={ASSETS.dashboard}
                alt="Visualisasi modern profil Sloto69"
                loading="lazy"
                className="rounded-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#070710] border-y border-white/5" data-testid="about-values">
        <div className="shell">
          <div className="max-w-2xl">
            <span className="gold-pill">Nilai-Nilai Kami</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Prinsip yang Mendasari Sloto69</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <article key={v.title} data-testid={`value-card-${i}`} className="card-premium p-6">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#10B981]/15 to-[#D4AF37]/10 border border-white/10">
                  <v.icon className="w-5 h-5 text-[#10B981]" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{v.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
